import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  BarChart3, ArrowLeft, Loader2, TrendingUp, Calendar, DollarSign
} from 'lucide-react';

export default function ExecutivoForecast() {
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState(null);
  const [params, setParams] = useState({
    ano: new Date().getFullYear() + 1,
    cenario: 'base',
    taxa_crescimento: 5,
    taxa_inadimplencia: 8
  });
  
  const navigate = useNavigate();

  const handleGerar = async () => {
    setLoading(true);
    try {
      const response = await api.post('/executivo/forecast', {
        ...params,
        taxa_crescimento: params.taxa_crescimento / 100,
        taxa_inadimplencia: params.taxa_inadimplencia / 100
      });
      setDados(response.data);
    } catch (error) {
      toast.error('Erro ao gerar projeção');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `R$ ${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="p-6 space-y-6" data-testid="executivo-forecast">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/executivo')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Forecast e Projeções
          </h1>
          <p className="text-muted-foreground">Cenários financeiros para planejamento</p>
        </div>
      </div>

      {/* Parâmetros */}
      <Card>
        <CardHeader>
          <CardTitle>Premissas da Projeção</CardTitle>
          <CardDescription>Ajuste os parâmetros para gerar cenários</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>Ano da Projeção</Label>
              <Input
                type="number"
                value={params.ano}
                onChange={(e) => setParams(prev => ({ ...prev, ano: parseInt(e.target.value) || 2026 }))}
              />
            </div>

            <div className="space-y-4 md:col-span-2">
              <Label>Taxa de Crescimento: {params.taxa_crescimento}%</Label>
              <Slider
                value={[params.taxa_crescimento]}
                onValueChange={([v]) => setParams(prev => ({ ...prev, taxa_crescimento: v }))}
                min={-10}
                max={30}
                step={1}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>-10% (queda)</span>
                <span>+30% (crescimento)</span>
              </div>
            </div>

            <div className="space-y-4">
              <Label>Inadimplência Esperada: {params.taxa_inadimplencia}%</Label>
              <Slider
                value={[params.taxa_inadimplencia]}
                onValueChange={([v]) => setParams(prev => ({ ...prev, taxa_inadimplencia: v }))}
                min={0}
                max={25}
                step={1}
              />
            </div>
          </div>

          <Button onClick={handleGerar} disabled={loading} className="w-full md:w-auto">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Calculando...
              </>
            ) : (
              <>
                <TrendingUp className="w-4 h-4 mr-2" />
                Gerar Projeção
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Resultados */}
      {dados && (
        <div className="space-y-6">
          {/* Dados Base */}
          <Card className="bg-gray-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Ano Base: {dados.ano_base}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Receita Total</p>
                  <p className="text-xl font-bold">{formatCurrency(dados.dados_base?.receita_total)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Custos Totais</p>
                  <p className="text-xl font-bold">{formatCurrency(dados.dados_base?.custos_total)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Inadimplência</p>
                  <p className="text-xl font-bold text-orange-600">{formatCurrency(dados.dados_base?.inadimplencia_total)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cenários */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Conservador */}
            {dados.cenarios?.conservador && (
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader>
                  <CardTitle className="text-amber-800">Cenário Conservador</CardTitle>
                  <CardDescription>+{dados.cenarios.conservador.crescimento_percentual}% crescimento</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Receita Projetada</p>
                    <p className="text-xl font-bold">{formatCurrency(dados.cenarios.conservador.receita_projetada)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">EBITDA</p>
                    <p className="text-xl font-bold text-amber-700">{formatCurrency(dados.cenarios.conservador.ebitda)}</p>
                    <p className="text-xs text-muted-foreground">Margem: {dados.cenarios.conservador.margem_ebitda}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Lucro Projetado</p>
                    <p className="text-xl font-bold">{formatCurrency(dados.cenarios.conservador.lucro_projetado)}</p>
                    <p className="text-xs text-muted-foreground">Margem: {dados.cenarios.conservador.margem_liquida}%</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Base */}
            {dados.cenarios?.base && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-blue-800">Cenário Base</CardTitle>
                  <CardDescription>+{dados.cenarios.base.crescimento_percentual}% crescimento</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Receita Projetada</p>
                    <p className="text-xl font-bold">{formatCurrency(dados.cenarios.base.receita_projetada)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">EBITDA</p>
                    <p className="text-xl font-bold text-blue-700">{formatCurrency(dados.cenarios.base.ebitda)}</p>
                    <p className="text-xs text-muted-foreground">Margem: {dados.cenarios.base.margem_ebitda}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Lucro Projetado</p>
                    <p className="text-xl font-bold">{formatCurrency(dados.cenarios.base.lucro_projetado)}</p>
                    <p className="text-xs text-muted-foreground">Margem: {dados.cenarios.base.margem_liquida}%</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Agressivo */}
            {dados.cenarios?.agressivo && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-green-800">Cenário Agressivo</CardTitle>
                  <CardDescription>+{dados.cenarios.agressivo.crescimento_percentual}% crescimento</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Receita Projetada</p>
                    <p className="text-xl font-bold">{formatCurrency(dados.cenarios.agressivo.receita_projetada)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">EBITDA</p>
                    <p className="text-xl font-bold text-green-700">{formatCurrency(dados.cenarios.agressivo.ebitda)}</p>
                    <p className="text-xs text-muted-foreground">Margem: {dados.cenarios.agressivo.margem_ebitda}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Lucro Projetado</p>
                    <p className="text-xl font-bold">{formatCurrency(dados.cenarios.agressivo.lucro_projetado)}</p>
                    <p className="text-xs text-muted-foreground">Margem: {dados.cenarios.agressivo.margem_liquida}%</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Projeção Mensal */}
          {dados.projecao_mensal && (
            <Card>
              <CardHeader>
                <CardTitle>Projeção Mensal - Cenário Base</CardTitle>
                <CardDescription>Considerando sazonalidade típica de escola</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Mês</th>
                        <th className="text-right py-2">Receita Projetada</th>
                        <th className="text-right py-2">Fator Sazonalidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.projecao_mensal.map(m => (
                        <tr key={m.mes} className="border-b">
                          <td className="py-2 font-medium">{m.nome}</td>
                          <td className="py-2 text-right">{formatCurrency(m.receita_projetada)}</td>
                          <td className="py-2 text-right">
                            {m.fator_sazonalidade > 1 ? (
                              <span className="text-green-600">+{((m.fator_sazonalidade - 1) * 100).toFixed(0)}%</span>
                            ) : m.fator_sazonalidade < 1 ? (
                              <span className="text-red-600">{((m.fator_sazonalidade - 1) * 100).toFixed(0)}%</span>
                            ) : (
                              <span className="text-gray-600">Base</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Premissas */}
          <Card className="bg-gray-50">
            <CardHeader>
              <CardTitle>Premissas Utilizadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Taxa de Crescimento</p>
                  <p className="font-medium">{(dados.premissas?.taxa_crescimento_input * 100).toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Taxa de Inadimplência</p>
                  <p className="font-medium">{(dados.premissas?.taxa_inadimplencia_input * 100).toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Inflação de Custos</p>
                  <p className="font-medium">{(dados.premissas?.inflacao_custos * 100).toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
